/**
 * GoalModal — create/edit cel oszczędnościowy.
 * Pola: nazwa, kwota docelowa, opcjonalna deadline, ikona (z presetów), kolor.
 */
import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Icons } from '../Icons';
import { type Goal } from '../../features/goals/goalsStore';
import { createGoal, updateGoal, deleteGoal } from '../../features/goals/api';
import { useAuthStore } from '../../features/auth/authStore';
import { parseAmountInput, formatAmountForInput, haptic, safeColor } from '../../lib/utils';
import { getCategoryIcon } from '../../lib/categoryIcons';
import { showToast } from '../ui/Toast';

interface Props {
  goal: Goal | null;
  onClose: () => void;
}

const ICON_OPTIONS = ['piggy', 'travel', 'shopping', 'home', 'car', 'education', 'health', 'gift'];
const COLORS = ['#0ea5e9', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'];

export function GoalModal({ goal, onClose }: Props) {
  const family = useAuthStore((s) => s.family);

  const isEdit = !!goal;
  const [name, setName] = useState(goal?.name ?? '');
  const [amount, setAmount] = useState(goal ? formatAmountForInput(goal.target) : '');
  const [icon, setIcon] = useState(goal?.icon ?? 'piggy');
  const [color, setColor] = useState(goal?.color ?? COLORS[0]!);
  const [deadline, setDeadline] = useState(goal?.deadline ? goal.deadline.toISOString().slice(0, 10) : '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Podaj nazwę celu');
      return;
    }
    const target = parseAmountInput(amount);
    if (!target) {
      setError('Podaj kwotę docelową');
      return;
    }
    if (!family) return;
    const deadlineDate = deadline ? new Date(deadline) : null;
    setSubmitting(true);
    try {
      if (isEdit && goal) {
        await updateGoal(goal.id, { name: name.trim(), target, icon, color, deadline: deadlineDate });
        showToast('Zaktualizowano cel', 'success');
      } else {
        await createGoal({
          familyId: family.id,
          name: name.trim(),
          target,
          icon,
          color,
          deadline: deadlineDate,
        });
        showToast('Cel utworzony', 'success');
      }
      haptic('success');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się zapisać');
      haptic('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!goal) return;
    if (!window.confirm(`Usunąć cel "${goal.name}"? Wpłaty zostaną stracone.`)) return;
    setSubmitting(true);
    try {
      await deleteGoal(goal.id);
      showToast('Cel usunięty', 'info');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się usunąć');
      setSubmitting(false);
    }
  };

  const Icon = getCategoryIcon(icon);

  return (
    <Modal
      onClose={onClose}
      size="md"
      status="info"
      icon={
        <div className="w-16 h-16 rounded-full ring-8 ring-sky-500/20 dark:ring-sky-400/30 flex items-center justify-center" style={{ backgroundColor: `${color}22`, color: safeColor(color) }}>
          <Icon size={28} strokeWidth={2.25} />
        </div>
      }
      title={isEdit ? 'Edycja celu' : 'Nowy cel'}
      footer={
        <div className="flex gap-2">
          {isEdit && (
            <button type="button" onClick={handleDelete} disabled={submitting} className="px-3 py-3 rounded-xl border border-rose-200 dark:border-rose-500/30 text-rose-600 dark:text-rose-400 text-sm font-semibold hover:bg-rose-50 dark:hover:bg-rose-500/10 transition disabled:opacity-50">
              <Icons.Trash size={16} />
            </button>
          )}
          <button type="button" onClick={onClose} disabled={submitting} className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition disabled:opacity-50">Anuluj</button>
          <button type="button" onClick={handleSave} disabled={submitting} className="flex-[1.5] px-4 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-card transition disabled:opacity-50">{submitting ? 'Zapisuję...' : isEdit ? 'Zapisz' : 'Utwórz'}</button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Nazwa celu</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="np. Wakacje 2026"
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Kwota docelowa</label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="5000,00"
              className="w-full text-2xl font-bold tabular-nums px-4 py-3 pr-14 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-base font-semibold text-slate-400">zł</span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Ikona</label>
          <div className="grid grid-cols-4 gap-2">
            {ICON_OPTIONS.map((key) => {
              const Ic = getCategoryIcon(key);
              const sel = key === icon;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setIcon(key)}
                  aria-pressed={sel}
                  className={`h-12 rounded-xl flex items-center justify-center border transition ${sel ? 'border-sky-500 ring-2 ring-sky-500/20 bg-sky-50 dark:bg-sky-500/10' : 'border-slate-200 dark:border-slate-700'}`}
                >
                  <Ic size={20} className="text-slate-600 dark:text-slate-300" />
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Kolor</label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                aria-label={`Kolor ${c}`}
                aria-pressed={c === color}
                className={`w-10 h-10 rounded-full transition ${c === color ? 'ring-4 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 ring-sky-500' : ''}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Termin <span className="text-slate-400 normal-case font-normal">(opcjonalny)</span>
          </label>
          <input
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 text-sm"
          />
        </div>

        {error && (
          <div className="px-3 py-2.5 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-sm text-rose-700 dark:text-rose-300">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
